import {
  Dialog,
  formatMoney,
  formatRange,
  type Booking,
  type Plan,
  type PlanStop,
  type TicketLeg,
  live,
} from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { iataOf } from '../../lib/airports';
import { IncidentMark, replacedIn } from '../booking/Incident';
import { extrasOf, legMode, placeOn } from './Journeys';
import { legEnds } from './LegEnds';
import { lasting, minutesSaid, wallOf } from '../../lib/clock';
import { byRoad, kmApart } from '../../lib/map';
import { alongRoad, useRoads, wayAsk, wayKey } from '../../lib/roads';
import { ChangeMark, ModeIcon } from '../icons/ModeIcon';
import { typeCodeOf } from '../../lib/reference';
import { SeatMap } from '../booking/SeatMap';
import { TicketCode } from '../booking/TicketCode';
import { placeOf } from '../../lib/seats';
import { useSeatPlans } from '../booking/useSeatPlans';
import { WayTrack } from './WayTrack';

/** `Sun 10.01`: a day, `2027-01-10`. */
function daySaid(day: string, lang: string): string {
  const weekday = new Intl.DateTimeFormat(lang, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${day}T00:00:00Z`),
  );
  return `${weekday} ${String(Number(day.slice(8, 10)))}.${day.slice(5, 7)}`;
}

/** `Sun 10.01, 00:25`: an instant local in a zone, its day and time. */
function said(at: string, zone: string | null, lang: string): string {
  const local = wallOf(at, zone);
  return `${daySaid(local.day, lang)}, ${local.time}`;
}

interface Props {
  plan: Plan;
  /** The stop whose way on it is. */
  stop: PlanStop;
  /** The zone home time is said in. */
  home?: string;
  /** Keeps the plan a track imported for the way changed: without it, a track is only said. */
  onPlan?: ((plan: Plan) => void) | undefined;
}

/** When a way departs and arrives: as the way says, else as its ticket's first and last legs do. */
export function wayTimes(stop: PlanStop): {
  departureAt: string | null;
  arrivalAt: string | null;
} {
  const ticket = stop.bookings.find((b) => b.kind === 'ticket' && live(b));
  const all = ticket ? ticket.legs : [];
  // Its own flights, when the ticket flies on past a change: those leaving where it leaves.
  const from = iataOf(stop.out?.from?.name);
  const own = all.filter((leg) => leg.origin === from);
  const legs = own.length > 0 ? own : all;
  return {
    departureAt: stop.out?.departureAt ?? legs[0]?.departureAt ?? null,
    arrivalAt: stop.out?.arrivalAt ?? legs.at(-1)?.arrivalAt ?? null,
  };
}

/** The wait at a change: its mark, where when pointed at, and how long — `⇄ 4 h 45 min`. */
export function WayWait({ city, wait }: { city: string; wait: string }) {
  const { t } = useTranslation();
  return (
    <p className="way-wait">
      <ChangeMark label={t('way.changeAt', { city })} />
      <span className="way-wait-time">{wait}</span>
    </p>
  );
}

/**
 * A way on, all that is known of it (the route's line pressed, a day of travel): when it departs
 * and arrives, local at each end and at home, how long it takes and how far it goes, its flights
 * — each its number, airline, aircraft, terminal and gate, the seats — the wait at a change, when
 * check-in opens, and what it costs or whose price it is in.
 */
export function WayDetail({
  plan,
  stop,
  home = Intl.DateTimeFormat().resolvedOptions().timeZone,
  onPlan,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  // The flight whose seat map shows, if one.
  const [mapOf, setMapOf] = useState<{ index: number; icao: string; leg: TicketLeg } | null>(
    null,
  );
  // The ticket's QR code shown full size, to the reader at the door.
  const [showCode, setShowCode] = useState(false);
  const at = plan.stops.indexOf(stop);
  const next = plan.stops[at + 1];
  const out = stop.out;
  const planFor = useSeatPlans(
    stop.bookings
      .filter((b) => b.kind === 'ticket' && live(b))
      .slice(0, 1)
      .flatMap((b) => b.legs),
  );
  // On the ground, how far it goes along its road: Tallinn to Narva 210 km, not 195 straight.
  const ground =
    out?.from && out.to && byRoad(out.mode) ? { from: out.from, to: out.to } : null;
  const roads = useRoads(ground ? [wayAsk(ground.from, ground.to, out?.mode)] : []);
  const road = ground
    ? alongRoad(out?.mode, roads.get(wayKey(ground.from, ground.to, out?.mode)))
    : null;
  // No way said yet: said so, and where it is chosen.
  if (!out) {
    return (
      <div className="way-detail">
        <p className="quiet">{t('way.notChosen')}</p>
        <WayTrack plan={plan} stop={stop} onPlan={onPlan} />
      </div>
    );
  }
  const toZone = next ? next.zone : stop.zone;
  const code = (end: { name: string } | null) => iataOf(end?.name);
  const from = [stop.city, code(out.from)].filter((part) => part !== null).join(' ');
  const to = next ? [next.city, code(out.to)].filter((part) => part !== null).join(' ') : '…';
  const tickets = stop.bookings.filter((b) => b.kind === 'ticket' && live(b));
  // Tickets cancelled on it: kept struck through above the one bought in place, if any.
  const gone = stop.bookings.filter((b) => b.kind === 'ticket' && !live(b));
  // A change on the way is priced, and ticketed, at its journey's start.
  const start = stop.transfer
    ? plan.stops.slice(0, Math.max(at, 0)).findLast((s) => !s.transfer)
    : undefined;
  // The ticket this way is flown on: its own, or at a change its journey's.
  const ticket: Booking | undefined = (start ?? stop).bookings.find(
    (b) => b.kind === 'ticket' && live(b),
  );
  const all: TicketLeg[] = ticket ? ticket.legs : [];
  // Its own flights of the ticket, Sydney's SYD → AUH and not AUH → CDG after the change: by
  // where they leave. Its times and distance are theirs; the journey's start shows the whole
  // ticket, as its boarding passes are, and a change its own flights.
  const fromCode = code(out.from);
  const every = all.map((leg, index) => ({ leg, index }));
  const own = every.filter(({ leg }) => leg.origin === fromCode);
  // The way picked shows its own flights alone; a way none of whose flights leave from it, all.
  const shown = own.length > 0 ? own : every;
  const legs = (own.length > 0 ? own : every).map((s) => s.leg);
  const people = ticket ? ticket.passengers : [];
  // When it departs and arrives: as the way says, else as its own flights do.
  const first = legs[0];
  const last = legs.at(-1);
  // The flight in mind, until a ticket says its own.
  const planned = shown.length === 0 ? out.flight : null;
  const departureAt = out.departureAt ?? first?.departureAt ?? null;
  const arrivalAt = out.arrivalAt ?? last?.arrivalAt ?? null;
  const took =
    departureAt !== null && arrivalAt !== null
      ? lasting(departureAt, arrivalAt)
      : out.minutes === null
        ? null
        : `~${minutesSaid(out.minutes)}`;
  const flown = legs.flatMap((leg) => (leg.km === null ? [] : [leg.km]));
  // Its own track, when one was imported, says how far it really goes.
  const km = stop.track
    ? Math.round(stop.track.metres / 1000)
    : legs.length > 0 && flown.length === legs.length
      ? flown.reduce((sum, leg) => sum + leg, 0)
      : out.fuel !== null
        ? out.fuel.km
        : road
          ? out.mode === 'ferry'
            ? road.ferryKm
            : road.km
          : out.from !== null && out.to !== null
            ? Math.round(kmApart(out.from, out.to))
            : null;
  const price =
    tickets.length > 0
      ? formatMoney(
          {
            currency: plan.trip.currency,
            minor: tickets.reduce((sum, b) => sum + b.price.minor, 0),
          },
          lang,
        )
      : start
        ? t('plan.pricedAt', { city: start.city })
        : `${formatRange(out.estimate, lang, true)} · ${t('plan.est')}`;
  const time = (instant: string | null, zone: string | null) =>
    instant === null ? (
      <span className="quiet">—</span>
    ) : (
      <time
        dateTime={instant}
        title={t('way.home', { at: said(instant, home, lang), zone: home })}
      >
        {said(instant, zone, lang)}
      </time>
    );
  // Each time in the zone of the place it is at: the way's own at its stops, a flight's at its
  // airports.
  const fromZone = out.departureAt === null ? (first?.originZone ?? stop.zone) : stop.zone;
  const endZone = out.arrivalAt === null ? (last?.destinationZone ?? toZone) : toZone;
  const rules = ticket?.rules;
  const opens =
    rules?.onlineOpensH != null && departureAt !== null
      ? new Date(Date.parse(departureAt) - rules.onlineOpensH * 3_600_000).toISOString()
      : null;

  return (
    <div className="way-detail">
      <p className="way-detail-head">
        <ModeIcon mode={out.mode} size={18} />
        {/* The mark says how it goes; the word is for screen readers. */}
        <strong>
          <span className="visually-hidden">{t(`mode.${out.mode}`)} </span>
          {from} → {to}
        </strong>
      </p>
      {gone.map((b) => (
        <p key={b.id} className="way-cancelled">
          <s>{b.name}</s>
          <IncidentMark booking={b} replaced={replacedIn(plan, b)} />
        </p>
      ))}
      {ticket && ticket.replaces !== null && (
        <p className="quiet way-replaces">↳ {t('incident.replacement')}</p>
      )}
      {/* Only what is known: no row of a time not said. */}
      <dl className="way-facts">
        {(departureAt !== null || planned) && (
          <div>
            <dt>{t('ticket.departure')}</dt>
            <dd>
              {/* A flight in mind without its times: the day it leaves. */}
              {departureAt === null && planned ? (
                <time dateTime={planned.day}>{daySaid(planned.day, lang)}</time>
              ) : (
                time(departureAt, fromZone)
              )}
            </dd>
          </div>
        )}
        {arrivalAt !== null && (
          <div>
            <dt>{t('ticket.arrival')}</dt>
            <dd>
              {/* Its day said with its clock: no ⁺¹ beside a date. */}
              {time(arrivalAt, endZone)}
            </dd>
          </div>
        )}
        {ticket?.incident?.kind === 'late' && (
          // Late: its real times beside the planned ones.
          <div className="way-late">
            <dt>{t('incident.late')}</dt>
            <dd>
              {time(ticket.incident.leavesAt, fromZone)} →{' '}
              {time(ticket.incident.arrivesAt, endZone)}
            </dd>
          </div>
        )}
        {took !== null && (
          <div>
            <dt>{t('way.took')}</dt>
            <dd>{took}</dd>
          </div>
        )}
        {km !== null && (
          <div>
            <dt>{t('way.distance')}</dt>
            <dd>{t('journeys.km', { km: km.toLocaleString(lang) })}</dd>
          </div>
        )}
        <div>
          <dt>{t('plan.price')}</dt>
          <dd>{price}</dd>
        </div>
        {ticket?.reference && (
          <div>
            <dt>{t(out.mode === 'flight' ? 'bookingForm.pnr' : 'bookingForm.reference')}</dt>
            <dd className="mono">{ticket.reference}</dd>
          </div>
        )}
        {ticket?.qr && (
          <div>
            <dt>{t('ticketCode.field')}</dt>
            <dd>
              <button
                type="button"
                className="link-button"
                aria-label={t('ticketCode.show', { name: ticket.name })}
                onClick={() => {
                  setShowCode(true);
                }}
              >
                {t('ticketCode.open')}
              </button>
              {showCode && (
                <TicketCode
                  code={ticket.qr}
                  name={ticket.name}
                  reference={ticket.reference}
                  onClose={() => {
                    setShowCode(false);
                  }}
                />
              )}
            </dd>
          </div>
        )}
        {opens !== null && (
          <div>
            <dt>{t('way.checkIn')}</dt>
            <dd>{time(opens, fromZone)}</dd>
          </div>
        )}
      </dl>
      {planned && (
        <ol className="way-legs">
          <li>
            <p className="way-leg-head">
              <span className="mono">{planned.number}</span>
              {planned.airline !== null && <span className="quiet"> · {planned.airline}</span>}
            </p>
            {planned.vehicle !== null && <p className="quiet">{planned.vehicle}</p>}
          </li>
        </ol>
      )}
      {shown.length > 0 && (
        <ol className="way-legs">
          {shown.map(({ leg, index }, position) => {
            // Each traveller's seat, said where on the aircraft it is once its map has come.
            const plan = planFor(leg);
            const seats = people.flatMap((p) => {
              const said = placeOn(p, index, t);
              if (said === null) return [];
              const seat = p.seats[index] ?? null;
              const place = plan && seat !== null ? placeOf(plan, seat) : null;
              const picked = p.kinds[index] ?? null;
              return [
                {
                  name: p.name,
                  seat: said,
                  // A middle seat is said by itself, as the traveller would: no window either side.
                  // Without its seat map, where it is as picked.
                  place: !place
                    ? picked === null
                      ? null
                      : t(`seatMap.kind.${picked}`)
                    : place.kind === 'middle'
                      ? t('seatMap.kind.middle')
                      : t('seatMap.placeShort', {
                          kind: t(`seatMap.kind.${place.kind}`),
                          side: t(`seatMap.side.${place.side}`),
                        }),
                },
              ];
            });
            const after = shown[position + 1]?.leg;
            const icao = typeCodeOf(leg.vehicle ?? '', []);
            return (
              <li key={index}>
                {/* One flight: where and when it goes are said above; its number and who
                    flies it, its aircraft and its ends. */}
                <p className="way-leg-head">
                  {shown.length > 1 && (
                    <strong className="mono">
                      {leg.origin} → {leg.destination}
                      {' · '}
                    </strong>
                  )}
                  {leg.number !== null && <span className="mono">{leg.number}</span>}
                  {(leg.airlineName ?? leg.operator) !== null && (
                    <span className="quiet"> · {leg.airlineName ?? leg.operator}</span>
                  )}
                </p>
                <p className="quiet">
                  {[
                    shown.length === 1 || leg.departureAt === null || leg.arrivalAt === null
                      ? null
                      : `${wallOf(leg.departureAt, leg.originZone ?? stop.zone).time} → ${wallOf(leg.arrivalAt, leg.destinationZone ?? toZone).time} · ${lasting(leg.departureAt, leg.arrivalAt)}`,
                    leg.vehicle,
                    leg.registration,
                    ...extrasOf(leg, t),
                  ]
                    .filter((part) => part !== null)
                    .join(' · ')}
                </p>
                {legEnds(leg, legMode(leg, out.mode), t).length > 0 && (
                  <p className="way-leg-ends">{legEnds(leg, legMode(leg, out.mode), t)}</p>
                )}
                {seats.length > 0 && (
                  <ul className="way-seats">
                    {seats.map((s, at) => (
                      <li key={at}>
                        {s.name} <strong className="mono">{s.seat}</strong>
                        {s.place !== null && <span className="quiet"> · {s.place}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {icao !== null && (
                  <button
                    type="button"
                    className="button button-small"
                    onClick={() => {
                      setMapOf({ index, icao, leg });
                    }}
                  >
                    {t('seatMap.open')}
                  </button>
                )}
                {after?.departureAt && leg.arrivalAt && (
                  <WayWait
                    city={leg.destination}
                    wait={lasting(leg.arrivalAt, after.departureAt)}
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}
      {mapOf && (
        <Dialog
          open
          wide
          title={t('seatMap.openFor', {
            leg: `${mapOf.leg.origin} → ${mapOf.leg.destination}`,
          })}
          onClose={() => {
            setMapOf(null);
          }}
        >
          <SeatMap
            icao={mapOf.icao}
            airline={mapOf.leg.airline}
            marked={people.flatMap((p, at) => {
              const seat = p.seats[mapOf.index];
              return seat ? [{ seat, own: at === 0 }] : [];
            })}
          />
        </Dialog>
      )}
      <WayTrack plan={plan} stop={stop} onPlan={onPlan} />
    </div>
  );
}
